/**
 * Pure router for incoming realtime (SSE) events. Maps an event type +
 * payload to the side-effects useRealtime should run: which TanStack Query
 * keys to invalidate and whether to show a toast. Pure so it is unit-testable
 * without an EventSource or a QueryClient.
 */

export type RealtimeInvalidate =
  | 'all'
  | 'retailers-throttled'
  /** retailers + mappings + review-queue count + dashboard totals. */
  | 'mapping-done'
  /** The AI-app approval queue (Settings → AI apps). */
  | 'ai-pending';

export interface RealtimeToast {
  variant: 'info' | 'success';
  /** i18n key passed to t() with `vars`. */
  titleKey: string;
  vars: Record<string, unknown>;
}

export interface RealtimeAction {
  invalidate: RealtimeInvalidate;
  toast: RealtimeToast | null;
}

export function routeRealtimeEvent(
  type: string,
  payload: Record<string, unknown>,
): RealtimeAction {
  switch (type) {
    case 'ai_pending_changed':
      return { invalidate: 'ai-pending', toast: null };
    case 'mapping_progress':
      return { invalidate: 'retailers-throttled', toast: null };
    case 'mapping_completed':
      return {
        invalidate: 'mapping-done',
        toast: {
          variant: 'success',
          titleKey: 'sources.toast.mappingDone',
          vars: {
            name: (payload.retailer_name as string) ?? '',
            count: (payload.mapped as number) ?? 0,
          },
        },
      };
    case 'data_refreshed':
    default:
      return { invalidate: 'all', toast: null };
  }
}
